import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Lock, ServerCog, ShieldCheck } from 'lucide-react';
import { OverviewPanel } from './panels/OverviewPanel';
import { OutputsPanel } from './panels/OutputsPanel';
import { RailsPanel } from './panels/RailsPanel';
import { LoadsPanel } from './panels/LoadsPanel';
import { RoomsPanel } from './panels/RoomsPanel';
import { TransferPanel } from './panels/TransferPanel';
import { StatusPanel } from './panels/StatusPanel';
import { KeypadsTab } from './KeypadsTab';
import { CommandsPanel } from './panels/CommandsPanel';
import { MappingsPanel } from './panels/MappingsPanel';
import { DiagnosticsPanel } from './panels/DiagnosticsPanel';
import { EventDrawer } from './EventDrawer';
import { LinkChip } from './LinkChip';
import { TransportPicker } from './TransportPicker';
import { SecuritySheet } from './sheets/SecuritySheet';
import { LinkSheet } from './sheets/LinkSheet';
import {
  CONSOLE_GROUPS,
  CONSOLE_GROUP_LABEL,
  CONSOLE_TAB,
  CONSOLE_TABS,
} from '../../lib/rcu/catalog/navigation';
import type { ConsoleTabId } from '../../lib/rcu/catalog/navigation';
import { ISSUE_TARGET } from '../../lib/rcu/catalog/issue';
import type { IssueTarget, IssueTargetKind } from '../../lib/rcu/catalog/issue';
import { assertNever } from '../../lib/exhaustive';
import { useRcuConfigStore } from '../../stores/rcuConfigStore';
import { useRcuLinkStore } from '../../stores/rcuLinkStore';

export interface TechnicianConsoleProps {
  onExit: () => void;
}

/** Which tab explains a given issue target. */
const tabForTarget = (kind: IssueTargetKind): ConsoleTabId => {
  switch (kind) {
    case ISSUE_TARGET.RCU:
      return CONSOLE_TAB.OUTPUTS;
    case ISSUE_TARGET.RAIL:
    case ISSUE_TARGET.BRIDGE:
      return CONSOLE_TAB.RAILS;
    case ISSUE_TARGET.LOAD:
      return CONSOLE_TAB.LOADS;
    case ISSUE_TARGET.ROOM:
      return CONSOLE_TAB.ROOMS;
    default:
      return assertNever(kind);
  }
};

const PanelFor: React.FC<{ tab: ConsoleTabId; onSelectTarget: (target: IssueTarget) => void }> = ({
  tab,
  onSelectTarget,
}) => {
  switch (tab) {
    case CONSOLE_TAB.OVERVIEW:
      return <OverviewPanel onSelectTarget={onSelectTarget} />;
    case CONSOLE_TAB.OUTPUTS:
      return <OutputsPanel />;
    case CONSOLE_TAB.RAILS:
      return <RailsPanel />;
    case CONSOLE_TAB.LOADS:
      return <LoadsPanel />;
    case CONSOLE_TAB.ROOMS:
      return <RoomsPanel />;
    case CONSOLE_TAB.TRANSFER:
      return <TransferPanel />;
    case CONSOLE_TAB.STATUS:
      return <StatusPanel />;
    case CONSOLE_TAB.KEYPADS:
      return <KeypadsTab />;
    case CONSOLE_TAB.COMMANDS:
      return <CommandsPanel />;
    case CONSOLE_TAB.MAPPINGS:
      return <MappingsPanel />;
    case CONSOLE_TAB.DIAGNOSTICS:
      return <DiagnosticsPanel />;
    default:
      return assertNever(tab);
  }
};

/** The header's two halves, each a caption over its own row of tabs. */
const TabGroups: React.FC<{ tab: ConsoleTabId; onSelect: (tab: ConsoleTabId) => void }> = ({
  tab,
  onSelect,
}) => (
  <nav className="mx-auto mt-5 flex max-w-5xl flex-wrap items-end gap-x-8 gap-y-3">
    {CONSOLE_GROUPS.map((group) => (
      <div key={group} className="min-w-0">
        <p className="mb-1.5 text-[10px] font-medium uppercase tracking-[0.2em] text-white/35">
          {CONSOLE_GROUP_LABEL[group]}
        </p>
        <div className="flex flex-wrap gap-1">
          {CONSOLE_TABS.filter((entry) => entry.group === group).map((entry) => {
            const Icon = entry.icon;
            const isActive = entry.id === tab;
            return (
              <button
                key={entry.id}
                type="button"
                onClick={() => onSelect(entry.id)}
                className={`flex shrink-0 items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-[#D4A054] text-[#1A1A1A]'
                    : 'text-white/60 hover:bg-white/10 hover:text-white'
                }`}
              >
                <Icon className="h-4 w-4" />
                {entry.label}
              </button>
            );
          })}
        </div>
      </div>
    ))}
  </nav>
);

/**
 * The hidden technician console. Installer-facing, so it stays in English like
 * the rest of the commissioning surface.
 *
 * Its tabs are split in two: the first half defines the device (what is wired to
 * what, stored on the tablet), the second talks to it (what it reports and what
 * it can be told to do). Everything the device answers appears in the event
 * drawer along the bottom.
 */
export const TechnicianConsole: React.FC<TechnicianConsoleProps> = ({ onExit }) => {
  const config = useRcuConfigStore((state) => state.config);
  const link = useRcuLinkStore((state) => state.link);
  const [tab, setTab] = React.useState<ConsoleTabId>(CONSOLE_TAB.OVERVIEW);
  const [securityOpen, setSecurityOpen] = React.useState(false);
  const [linkOpen, setLinkOpen] = React.useState(false);
  const [feedOpen, setFeedOpen] = React.useState(false);

  const showTarget = (target: IssueTarget): void => setTab(tabForTarget(target.kind));
  const name = config.rcu.name.trim();

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <header className="bg-[#1A1A1A] px-6 pb-5 pt-6 text-white">
        <div className="mx-auto flex max-w-5xl flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.2em] text-[#D4A054]">
              <ServerCog className="h-4 w-4" />
              Technician console
            </p>
            <h1 className="mt-1 truncate font-display text-3xl">
              {name === '' ? 'RCU configuration' : name}
            </h1>
            <p className="mt-1 text-sm text-white/60">
              {config.rcu.serialNumber.trim() === ''
                ? 'No serial number yet'
                : `Serial ${config.rcu.serialNumber.trim()}`}{' '}
              · {config.rails.length} rails · {config.loads.length} loads · {config.rooms.length} rooms
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <LinkChip link={link} onClick={() => setLinkOpen(true)} />
            <TransportPicker variant="compact" />
            <button
              type="button"
              onClick={() => setSecurityOpen(true)}
              className="flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-sm text-white/80 transition-colors hover:border-[#D4A054] hover:text-[#D4A054]"
            >
              <ShieldCheck className="h-4 w-4" />
              Security
            </button>
            <button
              type="button"
              onClick={onExit}
              className="flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-sm text-white/80 transition-colors hover:border-[#D4A054] hover:text-[#D4A054]"
            >
              <Lock className="h-4 w-4" />
              Lock console
            </button>
          </div>
        </div>

        <TabGroups tab={tab} onSelect={setTab} />
      </header>

      <main className="mx-auto max-w-5xl px-6 pb-28 pt-6">
        <AnimatePresence mode="wait">
          <motion.div
            key={tab}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.18 }}
          >
            <PanelFor tab={tab} onSelectTarget={showTarget} />
          </motion.div>
        </AnimatePresence>
      </main>

      <SecuritySheet isOpen={securityOpen} onClose={() => setSecurityOpen(false)} />
      <LinkSheet isOpen={linkOpen} onClose={() => setLinkOpen(false)} />
      <EventDrawer isOpen={feedOpen} onToggle={() => setFeedOpen((open) => !open)} />
    </div>
  );
};

export default TechnicianConsole;
